import { useEffect, useState } from 'react';
import { useOptionalSkinSession } from '../../salon/context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../../salon/config/sessionAppData';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import { kioskConfigAuthHeaders } from '../../shared/kioskConfigAuth';
import { appendFranchiseThemeParams } from '../../shared/franchiseFlowAuth';
import {
  KIOSK_PAGE_KEYS,
  defaultConfigForKey,
} from '../utils/kioskLandingDefaults';
import { normalizeKioskLandingConfig } from '../utils/kioskLandingMedia';

function themeQuery() {
  return appendFranchiseThemeParams({ _: String(Date.now()) }).toString();
}

async function fetchPublicLandingConfig(pageKey) {
  const base = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) return null;
  const q = themeQuery();
  const url = `${base}/kiosk-landings/${encodeURIComponent(pageKey)}/public?${q}`;
  const res = await fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    headers: kioskConfigAuthHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data?.config) return normalizeKioskLandingConfig(data.config);
  return null;
}

async function fetchSessionLandingConfig(pageKey, token) {
  if (!token) return null;
  const q = themeQuery();
  const url = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/kiosk-landing/${encodeURIComponent(pageKey)}?${q}`;
  const res = await fetchWithTimeout(url, {
    timeoutMs: 15000,
    cache: 'no-store',
    credentials: 'include',
    headers: kioskConfigAuthHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data?.config) return normalizeKioskLandingConfig(data.config);
  return null;
}

/**
 * Prefer public salon-admin config (latest admin Save), then session app-data.
 */
async function fetchLandingConfig(pageKey, token) {
  const publicConfig = await fetchPublicLandingConfig(pageKey);
  if (publicConfig) return publicConfig;
  return fetchSessionLandingConfig(pageKey, token);
}

/**
 * Load DB-backed kiosk landing config for face_identity | salon_entry.
 * Safe outside SkinSessionProvider (e.g. franchise login) — uses public config only.
 */
export function useKioskLandingConfig(pageKey = KIOSK_PAGE_KEYS.FACE_IDENTITY) {
  const session = useOptionalSkinSession();
  const token = session?.token || null;
  const tokenReady = session ? session.tokenReady : true;
  const [config, setConfig] = useState(() => defaultConfigForKey(pageKey));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const sessionToken = tokenReady ? token : null;
        const next = await fetchLandingConfig(pageKey, sessionToken);
        if (!cancelled && next) setConfig(next);
      } catch (_) {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pageKey, token, tokenReady]);

  return { config, loading, token };
}
